import React, {
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AccessRights from "../../../../components/AccessRights/AccessRights";
import Button, { ButtonModeE } from "../../../../components/Button/Button";
import Icon from "../../../../components/Icon/Icon";
import { H1, P, Span } from "../../../../components/Text/Text";
import {
  AccessLevelE,
  DeliveryOrderStatusE,
  IconColorE,
  IconSizeE,
  StatusE,
  UserRoleE,
  VideoContentStatusE,
} from "../../../../models/enums";
import { VersionT } from "../../../../models/models";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { selectCurrentUser } from "../../../../store/selectors/authSelectors";
import { setHouseNum } from "../../../../store/slices/appSlice";
import { UNCLEAR_HOUSE_NUMBER_VALUE } from "../../../../utils/constants";
import { TOAST_DURATION } from "../../../../utils/helpers/constants";
import { canRemoveVersion } from "../../../../utils/helpers/canRemoveVersion";
import { isMobileOrTablet } from "../../../../utils/helpers/mediaQueries";
import usePopup from "../../../../utils/hooks/usePopup";
import { useTranslate } from "../../../../utils/hooks/useTranslate";
// spec-49 — the shared version-action handlers and the moved ⋮ menu.
import { useVersionActions } from "../../../../utils/hooks/useVersionActions";
import MobileActionsMenu from "./MobileActionsMenu";

import styles from "./styles.module.scss";

type VideoControlButtonsPropTypes = {
  asSponsorship?: boolean;
  isFetchingProject: boolean;
  isDeleting: boolean;
  projectStatus: StatusE;
  broadcastingDate: string | Date;
  refetchProject: () => void;
  deleteUploadedFile: ({ fileId }) => void;
  getProject: ({ id }) => void;
  projectId?: string;
  version: VersionT;
  isPreUploaded: boolean;
  isChannel5or9: boolean;
  isDuplicate?: boolean;
  // spec-37 — the delivery-service order's status, off `data.DeliveryServiceDetailsViewModel`.
  // Optional and undefined for every non-delivery-service caller (and for the campaign page, which
  // has no order in hand); `canRemoveVersion` only consults it at
  // WaitingForApprovalOfAdvertisingAgency for a delivery-service user, where undefined reads as
  // "not paid" — the backend is the real guard either way.
  orderStatus?: DeliveryOrderStatusE | null;
};

function RemoveFileControlButtons({ handleRemoveFile, removePopup, t }) {
  const [loading, setLoading] = useState(false);
  return (
    <div className={styles.footerButtonsWrapper}>
      <Button
        mode={ButtonModeE.OUTLINED}
        onClick={removePopup}
        disabled={loading}
      >
        Cancel
      </Button>
      <Button
        onClick={async () => {
          setLoading(true);
          await handleRemoveFile();
          toast(t`Successfully deleted`, {
            type: "success",
            autoClose: TOAST_DURATION,
          });
          setLoading(false);
          removePopup();
        }}
        useGlobalLoading={false}
        loading={loading}
        disabled={loading}
      >
        Remove
      </Button>
    </div>
  );
}

const VideoControlButtons: FC<VideoControlButtonsPropTypes> = (props) => {
  const {
    asSponsorship,
    isFetchingProject,
    isDeleting,
    projectStatus,
    version,
    deleteUploadedFile,
    projectId: deliveryId,
    isPreUploaded,
    isChannel5or9,
    refetchProject,
    isDuplicate,
    orderStatus,
  } = props;
  const {
    Id,
    AdId,
    fileFormat,
    HouseNumber,
    sponsershipHouseNumber,
    StatusName,
    DownloadAllowed,
    Name,
  } = version;
  const user = useAppSelector(selectCurrentUser);
  const { createPopup, removePopup } = usePopup();
  const { t } = useTranslate();
  const { pathname } = useLocation();
  const { projectId } = useParams();
  const projectDeliveryId = deliveryId || projectId;

  // spec-49 — download / FTP / house-number / not-suitable handlers are shared with the versions library.
  const {
    handleDownload,
    isDownloading,
    confirmSendToFTP,
    openMarkNotSuitableForBroadcasting,
    openSetHouseNumber,
  } = useVersionActions(version, {
    projectId: projectDeliveryId,
    refetchProject,
  });

  const dispatch = useAppDispatch();
  const [showBroadcasterUploadButton, setShowBroadcasterUploadButton] =
    useState(true);

  const handleShowUploadButton = useCallback(
    (e: CustomEvent) => {
      setShowBroadcasterUploadButton(e.detail);
    },
    [AdId],
  );

  useEffect(() => {
    const eventName = `${AdId}-upload-button`;
    document.addEventListener(eventName, handleShowUploadButton);
    return () => {
      document.removeEventListener(eventName, handleShowUploadButton);
    };
  }, [AdId]);

  useEffect(() => {
    dispatch(setHouseNum({ key: AdId, value: HouseNumber }));
  }, [HouseNumber]);

  const handleConfirmRemoveFile = () => {
    createPopup({
      header: <H1>Warning</H1>,
      body: <P>Do you really want to remove?</P>,
      footer: (
        <RemoveFileControlButtons
          handleRemoveFile={handleRemoveFile}
          removePopup={removePopup}
          t={t}
        />
      ),
      className: styles.sendToFTPPopupWrapper,
    });
  };

  const handleRemoveFile = async () => {
    await deleteUploadedFile({ fileId: Id });
    return await refetchProject();
  };

  const handleRemovePreUploadedFile = () => {
    const customEvent = new CustomEvent("uppy-remove-file", {
      detail: {
        name: `${AdId}.${fileFormat.toLowerCase()}`,
      },
    });
    document.dispatchEvent(customEvent);
  };

  const handleUploadFile = (e) => {
    const file = e.target.files[0];
    const fileName = AdId;

    const customEvent = new CustomEvent("uppy-add-file", {
      detail: {
        file,
        fileName,
      },
    });
    document.dispatchEvent(customEvent);
  };

  const handleUpload = () => {
    const input = document.getElementById(`file-input-${AdId}`);
    if (input) {
      input.click();
    }
    document.dispatchEvent(new CustomEvent("show-uploader"));
  };

  const showMobileOverflowMenu = isMobileOrTablet();

  const renderControls = (layout: "desktop" | "mobile") => {
    const isMobileRows = layout === "mobile";
    const iconNoPointer = isMobileRows
      ? ({ style: { pointerEvents: "none" as const } } as const)
      : undefined;

    const mobileRow = (
      rowKey: string,
      labelText: string,
      onActivate: () => void,
      iconEl: React.ReactNode,
      opts?: { disabled?: boolean }
    ) => (
      <div
        key={rowKey}
        className={styles.mobileMenuRow}
        role="menuitem"
        tabIndex={opts?.disabled ? -1 : 0}
        aria-disabled={opts?.disabled || undefined}
        onClick={() => {
          if (opts?.disabled) {
            return;
          }
          onActivate();
        }}
        onKeyDown={(e) => {
          if (opts?.disabled) {
            return;
          }
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onActivate();
          }
        }}
      >
        <span className={styles.mobileMenuRowIcon}>{iconEl}</span>
        <Span className={styles.mobileMenuRowLabel}>{labelText}</Span>
      </div>
    );

    return (
      <>
        <AccessRights
          accessFor={[UserRoleE.Broadcaster]}
          condition={StatusName === VideoContentStatusE.ENCODED && !isDuplicate}
        >
          {isMobileRows ? (
            mobileRow(
              "not-suitable",
              "Mark as not suitable for broadcasting",
              () => openMarkNotSuitableForBroadcasting(),
              <Icon
                name="reject"
                title="Mark this version as not suitable for broadcasting"
                color={IconColorE.DARK_GRAY}
                iconProps={iconNoPointer}
              />
            )
          ) : (
            <Icon
              name="reject"
              title="Mark this version as not suitable for broadcasting"
              onClick={() => openMarkNotSuitableForBroadcasting()}
              color={IconColorE.DARK_GRAY}
            />
          )}
        </AccessRights>
        {DownloadAllowed && !isFetchingProject && (
          <>
            {isMobileRows
              ? mobileRow(
                  "download",
                  "Download for broadcasting",
                  () => handleDownload(asSponsorship),
                  <Icon
                    name={isDownloading ? "loading" : "download"}
                    color={IconColorE.BLACK}
                    size={IconSizeE.M}
                    title="Download file for Broadcasting"
                    iconProps={iconNoPointer}
                  />,
                  { disabled: isDownloading }
                )
              : (
                <Icon
                  name={isDownloading ? "loading" : "download"}
                  color={IconColorE.BLACK}
                  size={IconSizeE.M}
                  onClick={() => handleDownload(asSponsorship)}
                  title="Download file for Broadcasting"
                />
              )}
          </>
        )}
        <AccessRights
          accessFor={[UserRoleE.PostProduction, UserRoleE.DeliveryService]}
          condition={
            projectStatus === StatusE.Opened &&
            !isPreUploaded &&
            pathname.includes("deliveries")
          }
        >
          {isMobileRows ? (
            <>
              <input
                type="file"
                onChange={handleUploadFile}
                accept=".mp4,.mxf"
                multiple={false}
                id={`file-input-${AdId}`}
                className={styles.hiddenFileInput}
              />
              {mobileRow("upload-file", "Upload file", handleUpload, (
                <Icon
                  name="upload"
                  title="Upload file"
                  iconProps={iconNoPointer}
                />
              ))}
            </>
          ) : (
            <>
              <input
                type="file"
                onChange={handleUploadFile}
                accept=".mp4,.mxf"
                multiple={false}
                id={`file-input-${AdId}`}
                className={styles.hiddenFileInput}
              />
              <Icon name="upload" title="Upload file" onClick={handleUpload} />
            </>
          )}
        </AccessRights>
        <AccessRights
          accessFor={[UserRoleE.PostProduction, UserRoleE.DeliveryService]}
          condition={
            projectStatus === StatusE.Opened &&
            isPreUploaded &&
            StatusName === VideoContentStatusE.WAITING
          }
        >
          {isMobileRows
            ? mobileRow(
                "remove-preupload",
                "Remove file",
                handleRemovePreUploadedFile,
                <Icon name="bin" title="Remove file" iconProps={iconNoPointer} />
              )
            : (
                <Icon
                  name="bin"
                  title="Remove file"
                  onClick={handleRemovePreUploadedFile}
                />
              )}
        </AccessRights>
        <AccessRights
          accessFor={[UserRoleE.Broadcaster]}
          condition={!asSponsorship && projectStatus !== StatusE.Rejected}
        >
          {isMobileRows
            ? mobileRow(
                "house-number",
                "Set house number",
                () => openSetHouseNumber(),
                <Icon
                  name={HouseNumber ? "hashFilled" : "hash"}
                  title="Set house number"
                  iconProps={iconNoPointer}
                />
              )
            : HouseNumber ? (
                <Icon
                  name="hashFilled"
                  title="Set house number"
                  onClick={() => openSetHouseNumber()}
                />
              ) : (
                <Button icon="hash" onClick={() => openSetHouseNumber()}>
                  Set house number
                </Button>
              )}
        </AccessRights>
        {((asSponsorship && sponsershipHouseNumber) ||
          (!asSponsorship &&
            HouseNumber &&
            HouseNumber !== UNCLEAR_HOUSE_NUMBER_VALUE)) &&
          user?.role === UserRoleE.Broadcaster &&
          StatusName === VideoContentStatusE.ENCODED &&
          (projectStatus === StatusE.ApprovedAndSentToBroadcaster ||
            projectStatus === StatusE.DownloadedByAllBroadcasters ||
            projectStatus === StatusE.DownloadedByBroadcaster) &&
          (isMobileRows ? (
            mobileRow(
              "send-ftp",
              "Send to FTP",
              () => confirmSendToFTP(asSponsorship, false),
              <Icon name="shareFTP" title="Send to FTP" iconProps={iconNoPointer} />
            )
          ) : (
            <div className={styles.sendToFTPWrapper}>
              <Button
                icon="shareFTP"
                onClick={() => confirmSendToFTP(asSponsorship, false)}
              >
                Send to FTP
              </Button>
            </div>
          ))}
        {/* spec-37 — the status/role/order rule moved into canRemoveVersion so it can be tested
            without rendering this component. Only the two axes that are local to it stay here. */}
        <AccessRights
          accessFor={[UserRoleE.PostProduction, UserRoleE.DeliveryService]}
          condition={
            canRemoveVersion(StatusName, projectStatus, user?.role, orderStatus) &&
            user?.accessLevel !== AccessLevelE.GlobalAdmin &&
            !isFetchingProject
          }
        >
          {isMobileRows
            ? mobileRow(
                "remove-version",
                !isDeleting ? "Remove version" : "Removing",
                () => {
                  if (!isDeleting) {
                    handleConfirmRemoveFile();
                  }
                },
                <Icon
                  name={!isDeleting ? "bin" : "loading"}
                  color={!isDeleting ? IconColorE.BLACK : IconColorE.GRAY}
                  size={IconSizeE.M}
                  title={!isDeleting ? "Remove version" : "Removing"}
                  iconProps={iconNoPointer}
                />,
                { disabled: isDeleting }
              )
            : (
                <Icon
                  name={!isDeleting ? "bin" : "loading"}
                  color={!isDeleting ? IconColorE.BLACK : IconColorE.GRAY}
                  size={IconSizeE.M}
                  onClick={() => !isDeleting && handleConfirmRemoveFile()}
                  title={!isDeleting ? "Remove version" : "Removing"}
                />
              )}
        </AccessRights>
        <AccessRights
          accessFor={[UserRoleE.Broadcaster, UserRoleE.AdvertisingAgency]}
          condition={
            (projectStatus === StatusE.DownloadedByBroadcaster ||
              projectStatus === StatusE.DownloadedByAllBroadcasters) &&
            isChannel5or9 &&
            fileFormat !== "MP4"
          }
        >
          {showBroadcasterUploadButton &&
            (isMobileRows ? (
              <>
                <input
                  type="file"
                  onChange={handleUploadFile}
                  accept=".mp4,.mxf"
                  multiple={false}
                  id={`file-input-${AdId}`}
                  className={styles.hiddenFileInput}
                />
                {mobileRow(
                  "upload-adapted",
                  "Upload adapted version for broadcasting",
                  handleUpload,
                  <Icon
                    name="upload"
                    title="Upload the last version for broadcasting after content adaptation"
                    iconProps={iconNoPointer}
                  />
                )}
              </>
            ) : (
              <>
                <input
                  type="file"
                  onChange={handleUploadFile}
                  accept=".mp4,.mxf"
                  multiple={false}
                  id={`file-input-${AdId}`}
                  className={styles.hiddenFileInput}
                />
                <Icon
                  name="upload"
                  title="Upload the last version for broadcasting after content adaptation"
                  onClick={handleUpload}
                />
              </>
            ))}
        </AccessRights>
      </>
    );
  };

  return (
    <div className={styles.controlButtons}>
      {showMobileOverflowMenu ? (
        <MobileActionsMenu>{renderControls("mobile")}</MobileActionsMenu>
      ) : (
        renderControls("desktop")
      )}
    </div>
  );
};

export default VideoControlButtons;
